import { useEffect, useRef, useState } from "react";
import { ListTodo, NotebookPen, Plus } from "lucide-react";
import { useQuickPanel } from "./QuickPanelProvider";
import { brandOrangeBg } from "../../utils/accent";

const CLASSE_POSICAO = {
  "bottom-center": "bottom-5 left-1/2 -translate-x-1/2",
  "bottom-right": "bottom-5 right-5",
  "bottom-left": "bottom-5 left-5",
  "top-center": "top-20 left-1/2 -translate-x-1/2",
  "top-right": "top-20 right-5",
  "top-left": "top-20 left-5",
};

/**
 * Botão flutuante do apoio rápido: só o ícone quando recolhido, abre para
 * [ícone | Tarefa | Nota] no hover (desktop) ou no primeiro toque (mobile).
 */
export default function QuickPanelFab() {
  const { prefs, carregado, aberto, abrir } = useQuickPanel();
  const [expandido, setExpandido] = useState(false);
  const timerAbre = useRef(null);
  const timerFecha = useRef(null);
  const raizRef = useRef(null);

  useEffect(() => {
    const fora = (e) => {
      if (raizRef.current && !raizRef.current.contains(e.target)) setExpandido(false);
    };
    document.addEventListener("pointerdown", fora);
    return () => document.removeEventListener("pointerdown", fora);
  }, []);

  useEffect(() => () => {
    clearTimeout(timerAbre.current);
    clearTimeout(timerFecha.current);
  }, []);

  if (!carregado || !prefs.enabled) return null;

  const entrar = () => {
    clearTimeout(timerFecha.current);
    timerAbre.current = setTimeout(() => setExpandido(true), 200);
  };
  const sair = () => {
    clearTimeout(timerAbre.current);
    timerFecha.current = setTimeout(() => setExpandido(false), 400);
  };

  const acao = (qual) => (e) => {
    abrir(qual, e.currentTarget);
    setExpandido(false);
  };

  return (
    <div
      ref={raizRef}
      className={`fixed ${CLASSE_POSICAO[prefs.position] || CLASSE_POSICAO["bottom-center"]} ${
        aberto ? "z-[1400]" : "z-[1400]"
      } print:hidden`}
      onMouseEnter={entrar}
      onMouseLeave={sair}
      onFocus={entrar}
      data-testid="quick-panel-fab"
    >
      <div
        className="flex items-center rounded-full border border-gray-200/80 bg-white/90 backdrop-blur-md shadow-lg shadow-gray-900/5 transition-all duration-200 ease-out p-1"
      >
        <button
          type="button"
          onClick={() => setExpandido((v) => !v)}
          title="Apoio rápido"
          aria-label="Apoio rápido"
          aria-expanded={expandido}
          className="h-9 w-9 rounded-full flex items-center justify-center text-white transition-transform hover:scale-105"
          style={brandOrangeBg()}
          data-testid="quick-panel-toggle"
        >
          <Plus size={18} />
        </button>

        <div
          className={`flex items-center gap-1 overflow-hidden transition-all duration-200 ease-out ${
            expandido ? "max-w-[260px] opacity-100 ml-0.5" : "max-w-0 opacity-0"
          }`}
        >
          <button
            type="button"
            onClick={acao("tasks")}
            tabIndex={expandido ? 0 : -1}
            className="h-9 px-3 rounded-full inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 whitespace-nowrap"
            data-testid="quick-panel-open-tasks"
          >
            <ListTodo size={16} /> Tarefa
          </button>
          <span className="h-5 w-px bg-gray-200" />
          <button
            type="button"
            onClick={acao("notes")}
            tabIndex={expandido ? 0 : -1}
            className="h-9 px-3 rounded-full inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 whitespace-nowrap"
            data-testid="quick-panel-open-notes"
          >
            <NotebookPen size={16} /> Nota
          </button>
        </div>
      </div>
    </div>
  );
}
